import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowDown, Users, Cpu, Scale, Infinity, ChevronRight, Lightbulb, BookOpen, MessageSquare, Download } from 'lucide-react';

const fade = {
  initial: { opacity: 0, y: 12 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// ── NAV — icon + native text for sharpness ──

function HCANav() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#07182D] border-b border-[#1a3554]" style={{ height: '72px' }}>
      <nav className="max-w-[1180px] mx-auto px-6 h-full flex items-center justify-between">
        <Link to="/hca" className="flex items-center gap-3" data-testid="hca-logo-link">
          <img src="/assets/hca-logo-icon.png" alt="HCA" className="h-9 w-auto" style={{ objectFit: 'contain' }} />
          <span className="text-[13px] font-semibold text-white tracking-[0.12em] leading-tight hidden sm:block">
            HUMAN CONTINUITY<br />ARCHITECTURE
          </span>
        </Link>
        <div className="flex items-center gap-5 md:gap-7">
          <a href="#inquiry" className="text-xs text-slate-300 hover:text-white transition-colors tracking-[0.12em] uppercase hidden sm:block">Ideas</a>
          <a href="#questions" className="text-xs text-slate-300 hover:text-white transition-colors tracking-[0.12em] uppercase hidden sm:block">Research</a>
          <a href="#join" className="text-xs text-slate-300 hover:text-white transition-colors tracking-[0.12em] uppercase hidden sm:block">Conversation</a>
          <span className="w-px h-4 bg-[#1a3554] hidden sm:block" />
          <Link to="/" className="text-xs text-[#169BD5] hover:text-white transition-colors tracking-wider">R. DALE BRUNET</Link>
        </div>
      </nav>
    </header>
  );
}

// ── HERO ──

function HeroSection() {
  return (
    <section className="relative pt-[72px] overflow-hidden" data-testid="hca-hero">
      {/* Full-width background image */}
      <div className="absolute inset-0">
        <img
          src="/assets/hca-hero-bg.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
        />
        {/* Dark overlay for text legibility — heavier on left where text lives */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#07182D]/90 via-[#07182D]/75 to-[#07182D]/40" />
        {/* Bottom fade into next section */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
      </div>

      <div className="relative z-10 max-w-[1180px] mx-auto px-6 py-20 md:py-28 lg:py-32">
        <div className="max-w-2xl">
          {/* HCA Business Header logo — hero mark */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          >
            <img
              src="/assets/hca-business-header.png"
              alt="Human Continuity Architecture"
              className="h-40 sm:h-48 md:h-56 w-auto"
              style={{ objectFit: 'contain' }}
              data-testid="hca-hero-logo"
            />
          </motion.div>

          <motion.span
            className="text-xs tracking-[0.25em] text-[#169BD5] uppercase mb-6 block font-medium"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            An Emerging Field of Study
          </motion.span>

          <motion.h1
            className="text-3xl sm:text-4xl md:text-[2.75rem] leading-[1.25] text-white mb-6"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            data-testid="hca-headline"
          >
            <span className="font-light">As technology becomes more capable,</span>
            <br />
            <span className="font-bold">what happens to the human role?</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-[#169BD5] font-semibold italic mb-8"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            What about me?
          </motion.p>

          <motion.p
            className="text-base text-slate-200 leading-[1.65] mb-8 max-w-lg"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          >
            Human Continuity Architecture (HCA) is an emerging field of study examining how human agency, judgment, participation, accountability, knowledge, labor, memory, and continuity change as capabilities once performed by people are increasingly shared with or transferred to technological systems.
          </motion.p>

          <motion.div className="mb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
            <p className="text-sm text-slate-300">Not anti-AI.&nbsp; Not anti-progress.</p>
            <p className="text-lg text-white font-semibold">A more deliberate future.</p>
          </motion.div>

          <motion.button
            className="text-xs text-[#169BD5] tracking-[0.18em] uppercase flex items-center gap-2 hover:text-white transition-colors font-medium"
            onClick={() => document.getElementById('inquiry')?.scrollIntoView({ behavior: 'smooth' })}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            data-testid="hca-explore-cta"
          >
            Explore HCA <ArrowDown className="w-3.5 h-3.5" />
          </motion.button>
        </div>
      </div>
    </section>
  );
}

// ── THE QUESTION + FOUR AREAS ──

function QuestionAndAreasSection() {
  return (
    <section id="inquiry" className="bg-white py-14 md:py-20" data-testid="hca-question-areas">
      <div className="max-w-[1180px] mx-auto px-6">

        <div className="grid md:grid-cols-2 gap-8 md:gap-16 mb-10">
          <motion.div {...fade}>
            <p className="text-xs tracking-[0.18em] text-[#006BB6] uppercase mb-4 font-semibold">The Technological Shift</p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A1930] leading-snug mb-4">
              Technology changes what machines can do.
            </h2>
            <p className="text-base text-[#334155] leading-[1.65]">
              HCA asks us to be equally deliberate about what happens to the humans.
            </p>
          </motion.div>

          <motion.div {...fade} className="md:border-l md:border-[#cbd5e1] md:pl-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A1930] tracking-tight mb-4">
              WHAT ABOUT ME?
            </h2>
            <p className="text-base text-[#334155] leading-[1.65] mb-3">
              "What about me?" is not a demand that the individual always prevail. It is a question of human agency: Where is the individual in the systems, decisions, technologies, and institutions increasingly shaping human life?
            </p>
            <p className="text-base text-[#334155] leading-[1.65]">
              HCA asks whether people remain meaningful participants in decisions that affect them—even when those systems operate at enormous scale.
            </p>
          </motion.div>
        </div>

        {/* Editorial image — human/machine intersection */}
        <motion.div className="mb-14 rounded-xl overflow-hidden" {...fade}>
          <img
            src="/assets/hca-human-machine.jpg"
            alt="Human and machine — the intersection HCA examines"
            className="w-full h-48 md:h-64 object-cover"
          />
        </motion.div>

        {/* Four Areas — compact 2×2 */}
        <motion.div {...fade}>
          <p className="text-xs tracking-[0.18em] text-[#006BB6] uppercase mb-5 font-semibold">Four Areas of Inquiry</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#e2e8f0] border border-[#e2e8f0] rounded-lg overflow-hidden">
            {[
              { icon: Users, title: 'People', desc: 'Human agency, participation, dignity, identity, authorship, and opportunity.' },
              { icon: Cpu, title: 'Technology', desc: 'Increasing capabilities, emerging systems, automation, artificial intelligence, and new possibilities.' },
              { icon: Scale, title: 'Choices', desc: 'Tradeoffs, consent, accountability, influence, judgment, and who gets to decide.' },
              { icon: Infinity, title: 'Tomorrow', desc: 'How human continuity can remain intentional as technological capability expands.' },
            ].map((a) => (
              <div key={a.title} className="bg-white p-6">
                <a.icon className="w-5 h-5 text-[#006BB6] mb-3" />
                <h3 className="text-sm font-bold text-[#0A1930] uppercase tracking-wider mb-2">{a.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── FOUNDATIONAL QUESTIONS ──

function FoundationalQuestions() {
  return (
    <section id="questions" className="bg-white py-4" data-testid="hca-foundational">
      <div className="max-w-[1180px] mx-auto px-6">
        <motion.div className="relative bg-[#07182D] rounded-2xl overflow-hidden" {...fade}>
          {/* Eye-perspective image as subtle background */}
          <div className="absolute inset-0 pointer-events-none">
            <img
              src="/assets/hca-eye-perspective.jpg"
              alt=""
              className="w-full h-full object-cover opacity-[0.12]"
            />
          </div>

          <div className="relative z-10 px-8 md:px-14 py-12 md:py-14">
            <p className="text-xs tracking-[0.25em] text-[#169BD5] uppercase mb-10 text-center font-medium">Three Foundational Questions</p>

            <div className="grid md:grid-cols-3 gap-8 md:gap-4 text-center mb-10">
              {[
                { phrase: 'I AM.', concepts: 'Existence. Identity. Presence.' },
                { phrase: 'I WAS.', concepts: 'Memory. Provenance. Continuity.' },
                { phrase: 'WHAT ABOUT ME?', concepts: 'Agency. Participation. Choice.' },
              ].map((q) => (
                <div key={q.phrase}>
                  <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight mb-2">{q.phrase}</h2>
                  <p className="text-sm text-slate-300 tracking-wide">{q.concepts}</p>
                </div>
              ))}
            </div>

            <div className="w-10 h-px bg-[#169BD5] mx-auto mb-6" />
            <p className="text-[15px] text-slate-300 leading-[1.65] text-center max-w-2xl mx-auto">
              Together, these questions begin to frame an inquiry into what should remain meaningfully human as technology assumes capabilities that once belonged primarily to people.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── EIGHT BILLION + PROVE US WRONG ──

function ProtagonistsAndMethodology() {
  return (
    <section className="bg-white py-14 md:py-18" data-testid="hca-protagonists-method">
      <div className="max-w-[1180px] mx-auto px-6">

        <div className="grid md:grid-cols-[0.45fr_1fr] gap-8 md:gap-14 items-start mb-14">
          <motion.div {...fade}>
            <div className="relative rounded-xl overflow-hidden mb-4">
              <img
                src="/assets/hca-crowd.jpg"
                alt="Eight billion people — each the protagonist of their own story"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1930]/60 to-transparent" />
              <span className="absolute bottom-4 left-4 text-5xl md:text-6xl font-bold text-white/90 leading-none">8B</span>
            </div>
            <p className="text-xs tracking-[0.18em] text-[#006BB6] uppercase mt-2 font-semibold">Protagonists</p>
          </motion.div>
          <motion.div {...fade}>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A1930] mb-5">
              Eight billion protagonists.
            </h2>
            <p className="text-base text-[#1e293b] font-medium mb-4">
              Humanity is collective. Human experience is individual.
            </p>
            <div className="space-y-4 text-base text-[#334155] leading-[1.65]">
              <p>Every person experiences the world from the center of a life that matters to them.</p>
              <p>HCA explores what happens when systems designed for billions of people inevitably abstract those individuals into users, customers, citizens, workers, patients, datasets, demographics, and averages.</p>
              <p>The challenge is not eliminating scale.</p>
              <p className="text-[#1e293b] font-medium">The challenge is ensuring that the human being does not disappear inside it.</p>
            </div>
          </motion.div>
        </div>

        {/* Prove Us Wrong */}
        <motion.div className="border-t border-[#cbd5e1] pt-10" {...fade}>
          <div className="grid md:grid-cols-[1fr_0.55fr] gap-8 md:gap-14 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A1930] mb-1">This is what we think.</h2>
              <p className="text-xl md:text-2xl text-[#006BB6] font-semibold mb-6">Prove us wrong.</p>
              <div className="space-y-4 text-base text-[#334155] leading-[1.65]">
                <p>Human Continuity Architecture is not being presented as finished doctrine. It is an evolving investigation. Ideas will be proposed, researched, challenged, tested, revised, and sometimes discarded.</p>
                <p>The objective is not to defend every idea. The objective is to discover which ideas survive serious examination.</p>
              </div>
              <p className="text-2xl md:text-3xl text-[#0A1930] font-semibold italic leading-snug mt-6">I could be wrong.</p>
            </div>
            <div className="hidden md:block">
              <img
                src="/assets/hca-digital-future.jpg"
                alt="The evolving intersection of human identity and digital systems"
                className="w-full rounded-xl object-cover aspect-[3/4]"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ── AUTHOR + JOIN ──

function AuthorAndJoin() {
  return (
    <section id="join" className="bg-[#F5F8FB] border-t border-[#e2e8f0] py-14 md:py-16" data-testid="hca-author-join">
      <div className="max-w-[1180px] mx-auto px-6">

        <motion.div className="grid md:grid-cols-[1fr_0.85fr] gap-8 md:gap-14 items-start mb-12" {...fade}>
          <div>
            <p className="text-xs tracking-[0.18em] text-[#006BB6] uppercase mb-3 font-semibold">From the Researcher</p>
            <h2 className="text-xl md:text-2xl font-bold text-[#0A1930] mb-4">
              Why am I asking these questions?
            </h2>
            <div className="space-y-3 text-base text-[#334155] leading-[1.65]">
              <p>R. Dale Brunet has spent more than four decades working with technology—as a developer, entrepreneur, business owner, author, and increasingly as a student of artificial intelligence and its implications for ordinary human life.</p>
              <p>Human Continuity Architecture grew not from an assertion that he already had the answers, but from years of asking questions about technology, people, institutions, memory, agency, and the future.</p>
            </div>
            <Link to="/about" className="text-xs text-[#006BB6] hover:text-[#0A1930] transition-colors tracking-[0.12em] uppercase flex items-center gap-1 mt-5 font-semibold">
              About R. Dale Brunet <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="flex flex-col gap-6">
            <div className="rounded-xl overflow-hidden">
              <img
                src="/assets/hca-reflective-child.jpg"
                alt="A child gazing toward the future — the continuity HCA seeks to protect"
                className="w-full aspect-[3/2] object-cover"
              />
            </div>
            <div className="border-l-4 border-[#006BB6] pl-6">
              <p className="text-xl md:text-2xl text-[#0A1930] font-semibold italic leading-snug">
                I am not claiming to be.<br />I am becoming.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Join the Inquiry — with conversation image */}
        <motion.div className="border-t border-[#e2e8f0] pt-10" {...fade}>
          <div className="grid md:grid-cols-[1fr_0.8fr] gap-10 md:gap-14 items-center mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0A1930] mb-3">Join the inquiry.</h2>
              <p className="text-base text-[#475569] mb-8 max-w-xl">
                You do not have to agree with the premises. In fact, thoughtful disagreement is part of the work.
              </p>

              <div className="grid sm:grid-cols-3 gap-6">
                {[
                  { icon: Lightbulb, title: 'Ideas', desc: 'Propositions, frameworks, and emerging concepts worth exploring and challenging.' },
                  { icon: BookOpen, title: 'Research', desc: 'Investigations, evidence, and the evolving body of HCA inquiry.' },
                  { icon: MessageSquare, title: 'Conversation', desc: 'A space for questions, disagreement, and the exchange of perspectives.' },
                ].map((p) => (
                  <div key={p.title} className="text-center sm:text-left">
                    <div className="w-10 h-10 rounded-full border-2 border-[#006BB6]/40 flex items-center justify-center mx-auto sm:mx-0 mb-3">
                      <p.icon className="w-4 h-4 text-[#006BB6]" />
                    </div>
                    <h3 className="text-sm font-bold text-[#0A1930] uppercase tracking-wider mb-2">{p.title}</h3>
                    <p className="text-sm text-[#475569] leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <motion.div className="hidden md:block rounded-xl overflow-hidden" {...fade}>
              <img
                src="/assets/hca-conversation.jpg"
                alt="Thoughtful conversation — the foundation of HCA inquiry"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>

          {/* Professional conversation pathway */}
          <motion.div className="pt-8 border-t border-[#e2e8f0]" {...fade}>
            <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto items-start mb-10">
              {/* One-Pager Download */}
              <a
                href="/assets/hca-one-pager.png"
                download="HCA-One-Pager.png"
                className="group flex items-center gap-4 p-5 rounded-xl border border-[#e2e8f0] hover:border-[#006BB6]/40 bg-white hover:bg-[#f0f6fc] transition-colors"
                data-testid="hca-one-pager-download"
              >
                <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-[#006BB6]/10 flex items-center justify-center">
                  <Download className="w-5 h-5 text-[#006BB6]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#0A1930] mb-0.5">HCA One-Pager</p>
                  <p className="text-xs text-[#475569]">Download the overview (PNG)</p>
                </div>
              </a>

              {/* Continue the conversation */}
              <div className="flex flex-col justify-center text-center md:text-left">
                <h3 className="text-lg md:text-xl font-semibold text-[#0A1930] mb-2">
                  Continue the conversation
                </h3>
                <p className="text-sm text-[#475569] leading-relaxed mb-4">
                  Interested in discussing HCA, inviting R. Dale Brunet to speak, or bringing these questions into your organization?
                </p>
                <Link
                  to="/schedule"
                  className="inline-flex items-center gap-2 text-xs tracking-[0.12em] uppercase text-[#006BB6] hover:text-[#0A1930] transition-colors font-bold"
                  data-testid="hca-work-with"
                >
                  Work With R. Dale Brunet <ChevronRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// ── FOOTER ──

function HCAFooter() {
  return (
    <footer className="bg-[#07182D] border-t border-[#1a3554] py-8" data-testid="hca-footer">
      <div className="max-w-[1180px] mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/assets/hca-logo-icon.png" alt="HCA" className="h-6" style={{ objectFit: 'contain' }} />
          <span className="text-xs text-slate-400 tracking-wider">People&nbsp; |&nbsp; Technology&nbsp; |&nbsp; Choices&nbsp; |&nbsp; Tomorrow</span>
        </div>
        <div className="flex items-center gap-4 text-xs text-slate-400">
          <span>An emerging field of study by R. Dale Brunet</span>
          <span className="hidden sm:inline">|</span>
          <Link to="/schedule" className="text-[#169BD5] hover:text-white transition-colors">Work With R. Dale Brunet</Link>
          <span className="hidden sm:inline">|</span>
          <Link to="/" className="text-[#169BD5] hover:text-white transition-colors">RDBRUNET.COM</Link>
        </div>
      </div>
      <div className="max-w-[1180px] mx-auto px-6 mt-4">
        <p className="text-[11px] text-slate-500 text-center sm:text-left">&copy; {new Date().getFullYear()} R. Dale Brunet. All rights reserved.</p>
      </div>
    </footer>
  );
}

// ── MAIN ──

export default function HCAPage() {
  useEffect(() => {
    document.title = 'Human Continuity Architecture | R. Dale Brunet';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Human Continuity Architecture (HCA) is an emerging field of study examining how human agency, judgment, participation, and continuity change as technological systems become more capable.');
    return () => { document.title = 'R. Dale Brunet | Technology Architect, AI Strategist, Founder, Author'; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-white" data-testid="hca-page">
      <HCANav />
      <HeroSection />
      <QuestionAndAreasSection />
      <FoundationalQuestions />
      <ProtagonistsAndMethodology />
      <AuthorAndJoin />
      <HCAFooter />
    </div>
  );
}
